/**
 * TikTok Tracking Utility
 * Implements dual tracking: Browser Pixel + Server-side Events API (CAPI)
 * with proper deduplication, PII hashing, and ttclid/ttp capture.
 */

// TikTok event types. This union must match what TikTok Events Manager expects
// (see the E-commerce funnel template on the CreatorVault-v1 pixel). If you add
// a new event here, also add it to the tiktok-events edge function's allow-list
// if we ever add one, and confirm the pixel wizard has that event configured.
export type TikTokEventType =
  | 'ViewContent'
  | 'CompleteRegistration'
  | 'Lead'
  | 'InitiateCheckout'
  | 'AddPaymentInfo'
  | 'Purchase'
  | 'AddToCart'
  | 'PlaceAnOrder'
  | 'Search'
  | 'AddToWishlist'
  | 'SubmitForm'
  | 'Contact'
  | 'Subscribe'
  | 'Download';

/**
 * Identify the current user in the browser pixel so that all subsequent
 * pixel events carry hashed identity data (email/external_id/phone).
 * TikTok's pixel hashes these client-side before shipping, so we can pass
 * plaintext here — but only after login/signup. Never call this with a
 * value the user hasn't actually consented to share.
 *
 * Safe to call more than once (idempotent — TikTok overwrites the stored
 * identity on each call).
 */
export function identifyTikTokUser(user: TikTokUserData): void {
  try {
    if (typeof window === 'undefined' || !window.ttq) return;
    const payload: Record<string, string> = {};
    if (user.email) payload.email = user.email.toLowerCase().trim();
    if (user.phone) payload.phone_number = user.phone;
    if (user.externalId) payload.external_id = user.externalId;
    if (Object.keys(payload).length === 0) return;
    window.ttq.identify(payload);
    if (isDev) {
      console.log('[TikTok Pixel] Identified user', Object.keys(payload));
    }
  } catch (err) {
    if (isDev) console.warn('[TikTok Pixel] identify failed:', err);
  }
}

interface TikTokUserData {
  email?: string;
  phone?: string;
  externalId?: string;
}

interface TikTokEventOptions {
  user?: TikTokUserData;
  properties?: Record<string, unknown>;
}

// Extend Window interface for ttq
declare global {
  interface Window {
    ttq?: {
      track: (event: string, data?: Record<string, unknown>) => void;
      page: () => void;
      identify: (data: Record<string, unknown>) => void;
    };
  }
}

const EDGE_FUNCTION_URL = 'https://txoojazdivnmgstunpic.supabase.co/functions/v1/tiktok-events';
const isDev = import.meta.env.DEV;

/**
 * Capture ttclid from URL params and store in localStorage
 * Should be called on app initialization
 */
export function captureTtclid(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const ttclid = params.get('ttclid');
    if (ttclid) {
      localStorage.setItem('ttclid', ttclid);
      if (isDev) {
        console.log('[TikTok] Captured ttclid:', ttclid);
      }
    }
  } catch (error) {
    if (isDev) {
      console.error('[TikTok] Error capturing ttclid:', error);
    }
  }
}

/**
 * Get stored ttclid from localStorage
 */
export function getTtclid(): string | null {
  try {
    return localStorage.getItem('ttclid');
  } catch {
    return null;
  }
}

/**
 * Get _ttp cookie value (TikTok first-party cookie)
 */
export function getTtpCookie(): string | null {
  try {
    const match = document.cookie.match(/_ttp=([^;]+)/);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

/**
 * Generate UUID for event deduplication
 * Same ID is used for both pixel and server events
 */
export function generateEventId(): string {
  return crypto.randomUUID();
}

/**
 * Fire browser pixel event
 */
function firePixelEvent(
  event: TikTokEventType,
  eventId: string,
  properties?: Record<string, unknown>
): void {
  try {
    if (typeof window !== 'undefined' && window.ttq) {
      window.ttq.track(event, { event_id: eventId, ...properties });
      if (isDev) {
        console.log('[TikTok Pixel] Fired event:', event, { eventId, properties });
      }
    } else if (isDev) {
      console.warn('[TikTok Pixel] ttq not available');
    }
  } catch (error) {
    if (isDev) {
      console.error('[TikTok Pixel] Error firing event:', error);
    }
  }
}

/**
 * Send server-side event via edge function
 */
async function sendServerEvent(
  event: TikTokEventType,
  eventId: string,
  url: string,
  options?: TikTokEventOptions
): Promise<void> {
  try {
    const payload = {
      event,
      eventId,
      url,
      user: options?.user,
      properties: options?.properties,
      ttclid: getTtclid(),
      ttp: getTtpCookie(),
      userAgent: navigator.userAgent,
    };

    if (isDev) {
      console.log('[TikTok CAPI] Sending event:', payload);
    }

    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (isDev) {
      const result = await response.json();
      console.log('[TikTok CAPI] Response:', result);
    }
  } catch (error) {
    if (isDev) {
      console.error('[TikTok CAPI] Error sending event:', error);
    }
    // Silently fail in production - tracking should never break the app
  }
}

/**
 * Main tracking function - fires both browser pixel and server-side event
 * Uses the same eventId for deduplication
 * 
 * @param event - TikTok event type (ViewContent, CompleteRegistration, Lead)
 * @param options - Optional user data and properties
 * 
 * @example
 * // Track page view
 * trackTikTokEvent('ViewContent', {
 *   properties: { content_name: 'Homepage' }
 * });
 * 
 * @example
 * // Track registration with user data
 * trackTikTokEvent('CompleteRegistration', {
 *   user: { email: 'user@example.com', externalId: 'user_123' },
 *   properties: { registration_type: 'creator' }
 * });
 */
export async function trackTikTokEvent(
  event: TikTokEventType,
  options?: TikTokEventOptions
): Promise<void> {
  const eventId = generateEventId();
  const url = window.location.href;

  // Fire browser pixel event (synchronous)
  firePixelEvent(event, eventId, options?.properties);

  // Send server-side event (async, non-blocking)
  sendServerEvent(event, eventId, url, options).catch(() => {
    // Silently catch to prevent unhandled promise rejection
  });
}

/**
 * Stripe price -> friendly TikTok content metadata.
 * Keep this in sync with PRICE_IDS declared in Pricing.tsx / BusinessUpgrade.tsx /
 * UpgradeModal.tsx / DiscoverUpgradeModal.tsx. Unknown price IDs fall back to a
 * generic "subscription" content_id so we still get a signal.
 */
const PRICE_ID_META: Record<string, { name: string; value: number; cadence: 'month' | 'year' }> = {
  // v3 (current) — Starter/Growth/Scale
  price_1TmJNeBkrXY0Dxvlxnr79Vnf: { name: 'Starter monthly', value: 249, cadence: 'month' },
  price_1TmJNeBkrXY0DxvlfqjVMgrK: { name: 'Starter annual', value: 2388, cadence: 'year' },
  price_1TmJNfBkrXY0Dxvlk4kQEHoG: { name: 'Growth monthly', value: 499, cadence: 'month' },
  price_1TmJNgBkrXY0Dxvlhw1pLXwK: { name: 'Growth annual', value: 4788, cadence: 'year' },
  price_1TmJNgBkrXY0DxvlK2ytJAso: { name: 'Scale monthly', value: 999, cadence: 'month' },
  price_1TmJNhBkrXY0DxvlkCrDhZ7V: { name: 'Scale annual', value: 9588, cadence: 'year' },
};

export interface CheckoutContentMeta {
  content_id: string;
  content_name: string;
  content_type: 'subscription';
  currency: 'USD';
  value: number;
}

/**
 * Resolve TikTok content metadata for a Stripe priceId. Falls back to a generic
 * subscription entry when the priceId isn't in PRICE_ID_META (e.g. legacy v1/v2).
 */
export function checkoutMetaForPriceId(priceId: string): CheckoutContentMeta {
  const meta = PRICE_ID_META[priceId];
  return {
    content_id: priceId,
    content_name: meta ? `CreatorVault ${meta.name}` : 'CreatorVault subscription',
    content_type: 'subscription',
    currency: 'USD',
    value: meta?.value ?? 0,
  };
}

/**
 * Test function for admin dashboard
 * Sends a Lead event with test flag
 */
export async function sendTestTikTokEvent(): Promise<{ success: boolean; message: string }> {
  try {
    const eventId = generateEventId();
    const url = window.location.href;

    // Fire browser pixel
    firePixelEvent('Lead', eventId, { test: true, source: 'admin_test' });

    // Send server event and wait for response
    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        event: 'Lead',
        eventId,
        url,
        properties: { test: true, source: 'admin_test' },
        ttclid: getTtclid(),
        ttp: getTtpCookie(),
        userAgent: navigator.userAgent,
      }),
    });

    const result = await response.json();

    if (response.ok) {
      return { success: true, message: 'Test event sent successfully!' };
    } else {
      return { success: false, message: result.error || 'Failed to send test event' };
    }
  } catch (error) {
    return { 
      success: false, 
      message: error instanceof Error ? error.message : 'Unknown error occurred' 
    };
  }
}
